import { Suspense, useEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, useGLTF } from "@react-three/drei";

function AvatarModel({ avatarUrl }) {
    // Construct the URL - handle both full URL and just ID
    // Use a dummy URL if avatarUrl is empty to satisfy React hooks rules
    const modelUrl = avatarUrl 
        ? (avatarUrl.startsWith('http') ? avatarUrl : `https://models.readyplayer.me/${avatarUrl}.glb`)
        : null;
    
    // Hooks must be called unconditionally - but useGLTF needs a valid URL
    // Only call useGLTF if we have a valid URL, otherwise return null early
    // This is safe because the parent component already checks for avatarUrl
    if (!modelUrl) return null;
    
    // useGLTF handles async loading via Suspense
    const { scene } = useGLTF(modelUrl, true);
    
    // Clear cache on unmount/change
    useEffect(() => {
        return () => {
            // Clear cache when component unmounts or URL changes
            try {
                useGLTF.clear(modelUrl);
            } catch (err) {
                // Ignore cache clear errors
            }
        };
    }, [modelUrl]);

    // Adjust scale and position
    useEffect(() => {
        if (scene) {
            scene.scale.set(1.5, 1.5, 1.5);
            scene.position.set(0, -3.0, 0);
        }
    }, [scene]);

    return <primitive object={scene} scale={2} />;
}

export default function AvatarPreview({ avatarUrl }) {
    if (!avatarUrl) return null;

    return (
        <div className="relative w-64 h-64 border rounded-lg overflow-hidden">
            <Canvas
                camera={{ position: [0, -0.1, 3], fov: 30 }} // eye-level height, wider view
                style={{ width: "100%", height: "500px" }}
            >
                <ambientLight intensity={0.8} />
                <directionalLight position={[5, 5, 5]} />
                <Suspense
                    fallback={
                        <mesh>
                            <boxGeometry args={[0.5, 0.5, 0.5]} />
                            <meshStandardMaterial color="gray" />
                        </mesh>
                    }
                >
                    <AvatarModel avatarUrl={avatarUrl} />
                </Suspense>
                <OrbitControls enablePan={false} />
            </Canvas>
        </div>
    );
}
